// src/com/file_create_his/FileCreateHisTransportReport.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
// import { styled } from '@mui/material/styles';
import { Button, CircularProgress } from '@mui/material';
import ListAltIcon from '@mui/icons-material/ListAlt';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import DateRangePicker from '../../common/DateRangePicker';
import {
    ContentConainer, ContentHeader, ControlBox, ToolBox
} from '../../common/TopViewControl';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import {
    authTableTreeRepo, dcInfoListRepo, fchTransportReportListRepo, userInfoRepo
} from '../../value/AppModel';
import {
    LIST_FILE_CREATE_HIS_TRANSPORT_REPORT, REMOVE_FILE_CREATE_HIS
} from './FileCreateHisGql';
import { FileCreateHisDictionary } from './FileCreateHisDict';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import FlexyTable from '../../common/flexytable/FlexyTable';
import ValueMisc from '../../value/ValueMisc';
import TransPlanResultReportInput from '../file_of_report/TransPlanResultReportInput';
import FileCreateHisDownload from './FileCreateHisDownload';
import {
    TransPlanResultReportExcelColumns, TransPlanResultReportExcelColumnsForTranscom,
    TransPlanResultReportMeta, TransPlanResultReportMetaForTranscom
} from '../file_of_report/TransPlanResultReportDef';

const DaysToLoad = 7; // Default duration to load

/**
 * (FILE_CREATE_HIS) 정보 View Controller - 운송실적 리포트
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-10-11 14:19:23 using DbdbTap
 * GraphQL 인수를 repo에 넣으려면 TransPlanDownload.js 참조
 * 상온, 신선, 익스프레스 모두 작업 완료했음.
 * 운송사는? - 완전히 다른건지 먼저 확인하고 작업하자. ##########################
 */
export default function FileCreateHisTransportReport({
    forTruckCom = false
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const dcInfoListData = useReactiveVar(dcInfoListRepo); // for selection.
    const ReportMeta = useMemo(() => {
        if(sessionInfo.isTransCompanyUser()) {
            if(sessionInfo.isFreshUser(dcInfoListData.records)) return TransPlanResultReportMetaForTranscom.fresh;
            else if(sessionInfo.isExpressUser(dcInfoListData.records)) return TransPlanResultReportMetaForTranscom.express;
            else return TransPlanResultReportMetaForTranscom.accustom;
        }
        if(sessionInfo.isFreshUser(dcInfoListData.records)) return TransPlanResultReportMeta.fresh;
        else if(sessionInfo.isExpressUser(dcInfoListData.records)) return TransPlanResultReportMeta.express;
        else return TransPlanResultReportMeta.accustom;
    }, [sessionInfo, dcInfoListData]);
    const ReportExcelColumns = useMemo(() => {
        if(sessionInfo.isTransCompanyUser()) {
            if(sessionInfo.isFreshUser(dcInfoListData.records)) return TransPlanResultReportExcelColumnsForTranscom.fresh;
            else if(sessionInfo.isExpressUser(dcInfoListData.records)) return TransPlanResultReportExcelColumnsForTranscom.express;
            else return TransPlanResultReportExcelColumnsForTranscom.accustom;
        }
        if(sessionInfo.isFreshUser(dcInfoListData.records)) return TransPlanResultReportExcelColumns.fresh;
        else if(sessionInfo.isExpressUser(dcInfoListData.records)) return TransPlanResultReportExcelColumns.express;
        else return TransPlanResultReportExcelColumns.accustom;
    }, [sessionInfo, dcInfoListData]);
    const [duration, setDuration] = useState({dateFrom:'', dateTill:''});
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const fileCreateHisListData = useReactiveVar(fchTransportReportListRepo);
    const [openInputDialog, setOpenInputDialog] = useState(false);
    const [downloaderState, setDownloaderState] = useState({open:false, item:null, instructions:null});
    const openConfirmDialog = useConfirmDialog();
    const checkAuth = useAuthorizationCheck();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);
    const openCountRef = useRef(0);

    // ##### Call GraphQL to get List #####
    // 서버에서 세션 정보에 따라 fileFlag 값을 설정하여 조회. (운송사인가? 아닌가?)
    const [getFileCreateHisList, responseList] = useLazyQuery(LIST_FILE_CREATE_HIS_TRANSPORT_REPORT, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: FileCreateHisDictionary.errorTransportTitle.Get, message: [error.message]})
    });
    
    // ##### GraphQL Mutation. 'onError' for prevent from printing error at console (Uncaught exception) ###
    const [removeFileCreateHis, responseRemove] = useMutation( REMOVE_FILE_CREATE_HIS, {
		onCompleted: (data, option) => onCompleteRemove(data, option),
		onError: (error) => addAlert({title: FileCreateHisDictionary.errorTransportTitle.Remove, message: [error.message]})
	});

    const getFileCreateHisListNow = (dateFrom, dateTill) => {
        const param = {variables:{dateFrom:dateFrom, dateTill:dateTill}};
        getFileCreateHisList(param);
    };

    useEffect(() => {
        // set 3 days for first time loading
        if(openCountRef.current === 0) {
            openCountRef.current = 1;
            const dateTill = ValueMisc.getYMD();
            const dateFrom = ValueMisc.getYMD(ValueMisc.getDateAgo(DaysToLoad));
            setDuration({dateFrom:dateFrom, dateTill:dateTill});
            getFileCreateHisListNow(dateFrom, dateTill);
        }
    }, []);


    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.fileCreateHisTransportReportList) {
            const sec = new Date().getTime()/1000;
            fchTransportReportListRepo({when:sec, records:data.fileCreateHisTransportReportList});
            lastReloadRef.current = sec;
        }
    };

    const onCompleteRemove = (data, clientOption) => {
        if(data.fileCreateHisRemove.ok) getFileCreateHisListNow(duration.dateFrom, duration.dateTill);
        else
            addAlert({title:FileCreateHisDictionary.errorTransportTitle.Remove, message:[data.fileCreateHisRemove.message]});
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onChangeFrom = (ymd) => {
        setDuration({...duration, dateFrom:ymd});
        lastReloadRef.current = 0;
    };

    const onChangeTo = (ymd) => {
        setDuration({...duration, dateTill:ymd});
        lastReloadRef.current = 0;
    };

    const onClickLoad = () => {
        const diff = Math.round(new Date().getTime()/1000 - lastReloadRef.current);
        if(diff < 3) {
            // Too short to reload
            enqueueSnackbar(AppWord.ReloadNotIn3SecSameCond, {variant:'info', autoHideDuration:2000});
        }
        else {
            getFileCreateHisListNow(duration.dateFrom, duration.dateTill);
        }
    };

    const confirmRemove = async (item) => {
        const confirm = await openConfirmDialog({
            title: "운송실적 리포트 삭제",
            content: [
                `운송실적 리포트를 삭제합니다.`,
                '삭제 후 복구할 수 없습니다. 계속하시겠습니까?'
            ]
        });

        if(confirm)
            removeFileCreateHis({
                variables:{fileCreateHis:{fileCreateHisSeq:item.fileCreateHisSeq, serverUrl: item.serverUrl}}
            });
    };

    const onClickAdd = () => {
        setOpenInputDialog(true);
    };

    const onCompleteReportCreation = () => {
        getFileCreateHisListNow(duration.dateFrom, duration.dateTill);
        setOpenInputDialog(false);
    };

    const openDownloaderForItem = (item) => {
        const instructions = ReportMeta.instructions;
        setDownloaderState({open:true, item:item, instructions:instructions});
    }

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.DELETE: confirmRemove(row); break;
            case AppObject.command.DOWNLOAD: openDownloaderForItem(row); break;
            default: return;
        }
    };

    const canRemove = forTruckCom || checkAuth(authTableTree.tree.pr_file_create_his_remove);
    const tools = [
        {id:AppObject.command.DOWNLOAD, icon:<SimCardDownloadIcon/>, color: AppStyle.mui.primary, title:'다운로드'},
        //{id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.warning},
    ];
    if(canRemove) {
        tools.push(
            {id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.warning, title:'삭제'}
        );
    }

    const loadingAny = responseList.loading || responseRemove.loading;

    return(
        <ContentConainer>
            <ContentHeader>
                <ControlBox>
                    <DateRangePicker
                        dateFrom={duration.dateFrom}
                        dateTo={duration.dateTill}
                        onChangeFrom={onChangeFrom}
                        onChangeTo={onChangeTo}
                    />
                    {
                        responseRemove.loading ||
                        responseList.loading ?
                        <CircularProgress size={20} style={{marginLeft:10}}/>
                        :
                        <Button
                            startIcon={<ListAltIcon />}
                            onClick={onClickLoad}
                            variant={AppStyle.mui.contained}
                            style={{marginLeft:10}}
                            disabled={loadingAny}
                        >
                            조회
                        </Button>
                    }

                    {/* <SearchBox
                        value={searchText}
                        onChangeText={onChangeSearchText}
                    /> */}
                </ControlBox>
                <ToolBox>
                    <Button
                        startIcon={<NoteAddIcon />}
                        disabled={loadingAny}
                        variant={AppStyle.mui.contained}
                        style={{marginLeft:0}}
                        onClick={onClickAdd}
                    >
                        운송실적 리포트 생성
                    </Button>
                </ToolBox>
            </ContentHeader>
            <FlexyTable
                name={FileCreateHisDictionary.tableProps.tableId}
                tableProps={FileCreateHisDictionary.tableProps}
                records={fileCreateHisListData.records}
                tools={tools}
                onClickTool={onClickToolOnRow}
            />
            <TransPlanResultReportInput
                open={openInputDialog}
                onClose={() => setOpenInputDialog(false)}
                onComplete={onCompleteReportCreation}
            />
            <FileCreateHisDownload
                open={downloaderState.open}
                item={downloaderState.item}
                columns={ReportExcelColumns}
                instructions={downloaderState.instructions}
                getExtraHeaders={ReportMeta.getExtraHeaders}
                onClose={() => setDownloaderState({open:false, item:null, instructions:null})}
            />
        </ContentConainer>
    );
}
